import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useMemo, useState, type ComponentProps } from 'react';
import { z } from 'zod';
import { useItemIncidentList } from '@/hooks/useItemIncident';
import { ErrorState } from '@/components/feedback/ErrorState';
import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/Badge';
import type { ItemIncidentRow } from '@/lib/api';
import {
  EMPTY_FILTER,
  PERIOD_LABEL,
  SORT_LABEL,
  elapsedDays,
  isFiltered,
  openRowOptions,
  openRows,
  summarize,
  type GroupCounts,
  type ItemIncidentPeriod,
  type OpenRowFilter,
  type OpenRowSort,
} from '@/lib/item-incident';

/**
 * @graph-node {Page}
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business アイテム確認依頼ページ。インシデント対応シートのCSアイテム確認依頼を、サービス別・担当バイヤー別に未着手・対応中・完了で数え、未完了の依頼をキーワード・サービス・担当・着手区分で絞り込んで新しい順（切替可）に並べ、各依頼の詳細ページへ遷移させる
 * @graph-connects useItemIncidentList [calls] アイテム確認依頼一覧取得フック
 * @graph-connects ErrorState [calls] エラー表示描画
 */
export const Route = createFileRoute('/item-incident')({
  // 2026-10-30〜31 に投稿した Slack 通知は ?row=<シートの行番号> を指しているので、詳細ページへ転送する
  validateSearch: z.object({ row: z.coerce.number().int().min(4).optional().catch(undefined) }),
  beforeLoad: ({ search }) => {
    if (search.row !== undefined) {
      throw redirect({
        to: '/item-incident/$rowNumber',
        params: { rowNumber: String(search.row) },
        replace: true,
      }) as unknown as Error;
    }
  },
  component: ItemIncidentPage,
});

/** @graph-connects none */
const PERIODS: ItemIncidentPeriod[] = ['30d', '90d', '365d', 'all'];

/** これ以上経過した未完了依頼を強調する（回答の目安は3営業日） */
/** @graph-connects none */
const STALE_DAYS = 7;

/** 押せる要素に共通の見た目。カーソルを乗せると背景が変わり、押せる範囲が分かる */
/** @graph-connects none */
const CLICKABLE =
  'cursor-pointer transition-colors hover:bg-[var(--color-bg-hover)] focus-visible:outline-2 focus-visible:outline-[var(--color-border-focus)]';

/** @graph-connects none */
const FIELD_STYLE = {
  background: 'var(--color-bg-surface)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border)',
} as const;

/** @graph-connects none */
const STATUS_OPTIONS: { value: OpenRowFilter['status']; label: string }[] = [
  { value: null, label: 'すべて' },
  { value: 'notStarted', label: '未着手' },
  { value: 'inProgress', label: '対応中' },
];

/** @graph-connects none */
function todayJst(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** @graph-connects none */
function StatCard({ label, value, note }: { label: string; value: number; note: string }) {
  return (
    <GlassCard>
      <p className="text-xs font-semibold mb-1" style={{ color: 'var(--color-text-muted)' }}>
        {label}
      </p>
      <p
        className="text-3xl font-semibold tabular-nums"
        style={{ color: 'var(--color-text-primary)' }}
      >
        {value.toLocaleString()}
        <span className="ml-1 text-sm font-normal" style={{ color: 'var(--color-text-secondary)' }}>
          件
        </span>
      </p>
      <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
        {note}
      </p>
    </GlassCard>
  );
}

interface GroupTableProps {
  title: string;
  groups: GroupCounts[];
  selected: string | null;
  onSelect: (key: string | null) => void;
}

/** 行を押すと下の未完了一覧をその区分で絞る（もう一度押すと解除） */
/** @graph-connects none */
function GroupTable({ title, groups, selected, onSelect }: GroupTableProps) {
  return (
    <GlassCard>
      <p className="text-sm font-medium mb-3" style={{ color: 'var(--color-text-secondary)' }}>
        {title}
      </p>
      <table className="w-full text-sm" aria-label={title}>
        <thead>
          <tr style={{ color: 'var(--color-text-muted)' }}>
            <th className="text-left font-normal text-xs pb-2" />
            <th className="text-right font-normal text-xs pb-2">未着手</th>
            <th className="text-right font-normal text-xs pb-2">対応中</th>
            <th className="text-right font-normal text-xs pb-2">完了</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const active = selected === g.key;
            return (
              <tr
                key={g.key}
                className={CLICKABLE}
                style={active ? { background: 'var(--color-bg-active)' } : undefined}
                onClick={() => {
                  onSelect(active ? null : g.key);
                }}
              >
                <td className="py-1 pl-2 rounded-l-md">
                  <button
                    type="button"
                    aria-pressed={active}
                    onClick={(e) => {
                      // 行の onClick と二重に切り替わらないよう、ここで止める
                      e.stopPropagation();
                      onSelect(active ? null : g.key);
                    }}
                    className="text-left cursor-pointer"
                    style={{
                      color: 'var(--color-text-primary)',
                      fontWeight: active ? 600 : undefined,
                    }}
                  >
                    {g.key}
                  </button>
                </td>
                <td
                  className="py-1 text-right tabular-nums"
                  style={{
                    color: g.notStarted > 0 ? 'var(--color-error)' : 'var(--color-text-muted)',
                  }}
                >
                  {g.notStarted}
                </td>
                <td
                  className="py-1 text-right tabular-nums"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {g.inProgress}
                </td>
                <td
                  className="py-1 pr-2 text-right tabular-nums rounded-r-md"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {g.done}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GlassCard>
  );
}

/** 行全体を詳細ページへのリンクにする（文字だけでなく行のどこを押しても開く） */
/** @graph-connects none */
function OpenRow({ row, today }: { row: ItemIncidentRow; today: string }) {
  const days = elapsedDays(row.requestedOn, today);
  const stale = days !== null && days >= STALE_DAYS;
  return (
    <li style={{ borderTop: '1px solid var(--color-border)' }}>
      <Link
        to="/item-incident/$rowNumber"
        params={{ rowNumber: String(row.rowNumber) }}
        className={`group flex items-center gap-3 py-3 px-3 -mx-3 rounded-md ${CLICKABLE}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span
              data-row-title
              className="text-sm font-semibold group-hover:underline underline-offset-2"
              style={{ color: 'var(--color-primary)' }}
            >
              {row.no || `${String(row.rowNumber)}行目`} の詳細・回答
            </span>
            <Badge variant={row.statusCategory === 'notStarted' ? 'error' : 'warning'} size="sm">
              {row.status || '未着手'}
            </Badge>
            {row.priority === '高' && (
              <Badge variant="error" size="sm">
                優先度 高
              </Badge>
            )}
            <span
              className="text-xs tabular-nums"
              style={{ color: stale ? 'var(--color-error)' : 'var(--color-text-muted)' }}
            >
              {row.requestedOn ?? '起票日不明'}
              {days !== null && `（${String(days)}日経過）`}
            </span>
          </div>
          <p className="text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>
            担当 {row.checker || '未割当'} ／ {row.service} ／ {row.brand}
            {row.skuId && ` ／ SKU ${row.skuId}`} ／ 依頼者 {row.requester}
          </p>
          <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
            {row.bodyExcerpt}
          </p>
        </div>
        <span
          aria-hidden
          className="text-lg shrink-0 transition-transform group-hover:translate-x-0.5"
          style={{ color: 'var(--color-text-muted)' }}
        >
          ›
        </span>
      </Link>
    </li>
  );
}

interface FilterBarProps {
  filter: OpenRowFilter;
  onChange: (filter: OpenRowFilter) => void;
  sort: OpenRowSort;
  onSortChange: (sort: OpenRowSort) => void;
  services: string[];
  checkers: string[];
}

/** 標準の▼は枠に貼りついて窮屈なので消し、余白を取った自前の▼を重ねる */
/** @graph-connects none */
function FilterSelect({ className = '', children, ...props }: ComponentProps<'select'>) {
  return (
    <span className={`relative inline-flex ${className}`}>
      <select
        {...props}
        className="appearance-none rounded-md py-1.5 pl-3 pr-9 text-sm cursor-pointer"
        style={FIELD_STYLE}
      >
        {children}
      </select>
      <svg
        aria-hidden="true"
        viewBox="0 0 12 12"
        className="pointer-events-none absolute right-3 top-1/2 h-3 w-3 -translate-y-1/2"
        style={{ color: 'var(--color-text-muted)' }}
      >
        <path
          d="M2.5 4.5 6 8l3.5-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

/** 未完了一覧の絞り込み。キーワード・サービス・担当・着手区分と並び順 */
/** @graph-connects none */
function FilterBar({ filter, onChange, sort, onSortChange, services, checkers }: FilterBarProps) {
  return (
    <div className="flex flex-col gap-2 mb-3" role="search" aria-label="未完了の依頼の絞り込み">
      <input
        type="search"
        value={filter.keyword}
        onChange={(e) => {
          onChange({ ...filter, keyword: e.target.value });
        }}
        placeholder="No・ブランド・SKU・依頼者・担当・内容で検索（空白区切りで AND）"
        aria-label="キーワード"
        className="w-full rounded-md px-3 py-2 text-sm"
        style={FIELD_STYLE}
      />
      <div className="flex items-center gap-2 flex-wrap">
        <FilterSelect
          aria-label="サービス"
          value={filter.service ?? ''}
          onChange={(e) => {
            onChange({ ...filter, service: e.target.value || null });
          }}
        >
          <option value="">サービス: すべて</option>
          {services.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect
          aria-label="担当"
          value={filter.checker ?? ''}
          onChange={(e) => {
            onChange({ ...filter, checker: e.target.value || null });
          }}
        >
          <option value="">担当: すべて</option>
          {checkers.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </FilterSelect>
        <div
          role="group"
          aria-label="着手区分"
          className="inline-flex rounded-md overflow-hidden"
          style={{ border: '1px solid var(--color-border)' }}
        >
          {STATUS_OPTIONS.map((o) => {
            const active = filter.status === o.value;
            return (
              <button
                key={o.label}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  onChange({ ...filter, status: o.value });
                }}
                className={`px-3 py-1.5 text-xs font-medium ${CLICKABLE}`}
                style={{
                  background: active ? 'var(--color-bg-active)' : undefined,
                  color: active ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        <FilterSelect
          aria-label="並び順"
          value={sort}
          onChange={(e) => {
            onSortChange(e.target.value as OpenRowSort);
          }}
          className="ml-auto"
        >
          {(Object.keys(SORT_LABEL) as OpenRowSort[]).map((v) => (
            <option key={v} value={v}>
              {SORT_LABEL[v]}
            </option>
          ))}
        </FilterSelect>
        {isFiltered(filter) && (
          <button
            type="button"
            onClick={() => {
              onChange(EMPTY_FILTER);
            }}
            className={`px-3 py-1.5 text-xs rounded-md ${CLICKABLE}`}
            style={{
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-border)',
            }}
          >
            条件をクリア
          </button>
        )}
      </div>
    </div>
  );
}

/** @graph-connects quality-atelier-api [calls] Quality Atelier APIを呼び出す */
function ItemIncidentPage() {
  const { data, isLoading, isError, refetch } = useItemIncidentList();
  const [period, setPeriod] = useState<ItemIncidentPeriod>('90d');
  const [filter, setFilter] = useState<OpenRowFilter>(EMPTY_FILTER);
  const [sort, setSort] = useState<OpenRowSort>('newest');
  const today = todayJst();
  const rows = useMemo(() => data?.data.rows ?? [], [data]);
  const summary = useMemo(() => summarize(rows, period, today), [rows, period, today]);
  const open = useMemo(() => openRows(rows, filter, sort), [rows, filter, sort]);
  const options = useMemo(() => openRowOptions(rows), [rows]);
  const openTotal = useMemo(() => rows.filter((r) => r.statusCategory !== 'done').length, [rows]);
  const sheetUrl = data?.data.sheetUrl ?? '';

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            アイテム確認依頼
          </h1>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
            インシデント対応シート（CSアイテム確認依頼）の着手状況。ステータスが空欄の依頼を未着手として数えます。
          </p>
        </div>
        {sheetUrl && (
          <a
            href={sheetUrl}
            target="_blank"
            rel="noreferrer"
            className="text-sm underline underline-offset-2"
            style={{ color: 'var(--color-primary)' }}
          >
            シートを開く
          </a>
        )}
      </div>

      {isError && (
        <ErrorState
          title="データ取得エラー"
          message="インシデント対応シートの読み込みに失敗しました。"
          onRetry={() => {
            void refetch();
          }}
        />
      )}

      {!isError && isLoading && (
        <div className="glass-card p-5 skeleton-shimmer h-48" aria-label="読み込み中" />
      )}

      {!isError && !isLoading && (
        <>
          <div
            className="flex items-center gap-2 mb-3 flex-wrap"
            role="group"
            aria-label="完了件数の期間"
          >
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              完了の集計期間
            </span>
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={period === p}
                onClick={() => {
                  setPeriod(p);
                }}
                className={`px-3 py-1 text-xs font-medium rounded-md ${CLICKABLE}`}
                style={{
                  background: period === p ? 'var(--color-bg-active)' : 'transparent',
                  color: period === p ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                }}
              >
                {PERIOD_LABEL[p]}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatCard label="未着手" value={summary.totals.notStarted} note="いま残っている全件" />
            <StatCard label="対応中" value={summary.totals.inProgress} note="いま残っている全件" />
            <StatCard
              label="完了"
              value={summary.totals.done}
              note={`${PERIOD_LABEL[period]}に起票された分`}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <GroupTable
              title="サービス別"
              groups={summary.byService}
              selected={filter.service}
              onSelect={(service) => {
                setFilter((f) => ({ ...f, service }));
              }}
            />
            <GroupTable
              title="担当バイヤー別"
              groups={summary.byChecker}
              selected={filter.checker}
              onSelect={(checker) => {
                setFilter((f) => ({ ...f, checker }));
              }}
            />
          </div>

          <GlassCard>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                未完了の依頼
              </p>
              <Badge variant="info" size="sm">
                {isFiltered(filter)
                  ? `${String(open.length)}件 / ${String(openTotal)}件`
                  : `${String(open.length)}件`}
              </Badge>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {STALE_DAYS}
                日以上経過した依頼は日付を赤で示します。行を押すと全文の確認と回答ができます（回答は依頼者に
                Slack で届きます）。
              </p>
            </div>
            <FilterBar
              filter={filter}
              onChange={setFilter}
              sort={sort}
              onSortChange={setSort}
              services={options.services}
              checkers={options.checkers}
            />
            {open.length === 0 ? (
              <p className="text-sm py-3" style={{ color: 'var(--color-text-secondary)' }}>
                {isFiltered(filter)
                  ? '条件に合う未完了の依頼はありません。'
                  : '未完了の依頼はありません。'}
              </p>
            ) : (
              <ul aria-label="未完了の依頼">
                {open.map((row) => (
                  <OpenRow key={row.rowNumber} row={row} today={today} />
                ))}
              </ul>
            )}
          </GlassCard>
        </>
      )}
    </div>
  );
}
